import { useState, useEffect, useMemo } from 'react';
import {
  UserPropertyType,
  AuthenticatedUser,
  TableOverviewUser
} from '@gettactic/api';
import { useSaveUserSettings } from '@/hooks/useUserSettings';
import { useQueryClient } from '@tanstack/react-query';
import {
  DashboardFilters,
  useDashboardFilters
} from '@/contexts/DashboardStateContext';
import { useAuthenticated } from '@gettactic/hooks';
import { useTableOverview } from '@/hooks/useTableOverview';
import { TableOverviewParams } from '@gettactic/api';
import { format } from 'date-fns';

interface UseTeamAttendanceFiltersProps {
  authenticatedUser: AuthenticatedUser;
  currentOffice: { id: string } | undefined;
  onlyFilters?: boolean;
  selectedDayIndex?: number;
}

interface TeamAttendanceFiltersState {
  filters: DashboardFilters;
  defaultOffice: string;
  isComponentLoading: boolean;
  totalUsers: number;
  tableOverviewData: TableOverviewUser[];
  chartData: Array<{ name: string; inOffice: number; favorite: number }>;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  setOfficesId: (ids: string) => void;
  setTeams: (ids: string[]) => void;
  setUsersFilter: (ids: string[]) => void;
  handleSaveFilterData: (
    type: UserPropertyType,
    value: string | string[],
    onSettled?: () => void
  ) => void;
  sortBy: string;
  handleSortByChange: (date: string) => void;
}

export const useTeamAttendanceFilters = ({
  authenticatedUser,
  currentOffice,
  onlyFilters = false,
  selectedDayIndex
}: UseTeamAttendanceFiltersProps): TeamAttendanceFiltersState => {
  const queryClient = useQueryClient();
  const saveUserSettings = useSaveUserSettings(queryClient);
  const { setUserContext, userContext: { deviceTz } } = useAuthenticated();
  const { filters, setTeams, setUsers, setOffice } = useDashboardFilters();
  const todayDate = format(new Date(), 'yyyy-MM-dd');
  const [sortBy, setSortBy] = useState<string>(todayDate);
  // Extract all filter values to make sure we're tracking changes to any of them
  const { weekDate, week, teams, office, users, dateToSortBy, dateSortOrder } =
    filters;

  const [tableOverviewFilters, setTableOverviewFilters] =
    useState<TableOverviewParams>({
      office_id: currentOffice?.id ?? '',
      date: filters.weekDate ?? '',
      user_tz: deviceTz,
      limit: 30,
      team_id: teams || undefined,
      user_id: users || undefined,
      date_sort_order: dateSortOrder ?? undefined,
      date_sort: dateToSortBy ?? undefined
    });

  useEffect(() => {
    const newFilters = {
      office_id: currentOffice?.id ?? '',
      date: filters.weekDate ?? '',
      limit: 30,
      team_id: teams || undefined,
      user_id: users || undefined,
      user_tz: deviceTz,
      
      date_sort_order: dateSortOrder ?? undefined,
      date_sort: dateToSortBy ?? undefined,
      is_single_date: false
    };

    if (selectedDayIndex !== undefined && selectedDayIndex !== null) {
      const selectedDate = week?.[selectedDayIndex];
      if (selectedDate) {
        newFilters.date = format(selectedDate, 'yyyy-MM-dd');
        newFilters.date_sort = newFilters.date;
        newFilters.is_single_date = true;
      }
    }

    setTableOverviewFilters(newFilters);
  }, [
    filters.weekDate,
    currentOffice?.id,
    teams,
    users,
    sortBy,
    dateToSortBy,
    dateSortOrder,
    selectedDayIndex
  ]);

  const {
    data,
    error: tableOverviewError,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    allUsers,
    allDays,
    totalPeopleInOffice,
    totalFavoritePeopleInOffice
  } = useTableOverview(tableOverviewFilters, {
    enabled: !onlyFilters
  });

  const [isComponentLoading, setIsComponentLoading] = useState(false);

  // Calculate defaultOffice from authenticatedUser
  const defaultOffice = useMemo(() => {
    const settingsOfficeId = currentOffice?.id;
    return settingsOfficeId || authenticatedUser.offices?.offices[0]?.id || '';
  }, [currentOffice?.id]);

  // Update default office when needed
  useEffect(() => {
    if (defaultOffice && !office) {
      setOffice(defaultOffice);
    }
  }, [defaultOffice, office, setOffice]);

  const handleSaveFilterData = (
    type: UserPropertyType,
    value: string | string[],
    onSettled?: () => void
  ) => {
    setIsComponentLoading(true);
    const valueToSave =
      type === UserPropertyType.LastOfficesFilter ? [value] : value;
    if (type === UserPropertyType.LastOfficesFilter && valueToSave.length > 0) {
      setUserContext({
        type: 'updateCurrentOffice',
        payload: Array.isArray(valueToSave)
          ? (valueToSave[0] as string)
          : valueToSave
      });
    }
    saveUserSettings.mutate(
      { type, value: JSON.stringify(valueToSave) },
      {
        onSuccess: () => {
          onSettled?.();
        },
        onSettled: () => {
          setIsComponentLoading(false);
        }
      }
    );
  };

  // Fetch working location data
  const date = weekDate ?? null;
  if (!date) {
    return {
      filters,
      defaultOffice: '',
      isComponentLoading: false,
      totalUsers: 0,
      tableOverviewData: [],
      chartData: [],
      hasNextPage: false,
      fetchNextPage: () => {},
      isFetchingNextPage: false,
      isLoading: false,
      setTeams,
      setOfficesId: setOffice,
      setUsersFilter: setUsers,
      handleSaveFilterData,
      sortBy: '',
      handleSortByChange: () => {}
    };
  }

  const chartData = useMemo(() => {
    return Object.keys(allDays).map((dateStr) => {
      const daily = allDays[dateStr];
      if (!daily) {
        return {
          name: dateStr,
          inOffice: 0,
          favorite: 0
        };
      }
      return {
        name: dateStr,
        inOffice: daily.people_in_office,
        favorite: daily.favorite_people_in_office
      };
    });
  }, [allDays]);

  return {
    filters,
    defaultOffice,
    isComponentLoading,
    totalUsers: totalPeopleInOffice ?? 0,
    tableOverviewData: allUsers ?? [],
    chartData,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
    isLoading: isFetching,
    setTeams,
    setOfficesId: async (ids: string) => {
      handleSaveFilterData(UserPropertyType.LastOfficesFilter, ids, () =>
        setOffice(ids)
      );
    },
    setUsersFilter: setUsers,
    handleSaveFilterData,
    sortBy,
    handleSortByChange: setSortBy
  };
};
